/**
 * Voucher Mutations
 *
 * Mutation hooks for voucher-related operations via the Marketing Service (Federation Gateway).
 *
 * @module @kidslabs/module-flash-sale/api/mutations/voucher.mutation
 */

import {
    useMarketingApplyCodeMutation,
    useMarketingRemoveCodeMutation,
    useMarketingApplyVoucherFromAllocationMutation,
    useMarketingRemoveAppliedVoucherMutation,
} from '../../types/marketing.graphql';

/**
 * Hook for applying a code (voucher or coupon) via Marketing Service.
 *
 * Uses the unified `marketingApplyCode` mutation which auto-detects
 * whether the code is a voucher or coupon.
 */
export const useMarketingApplyCode = () => {
    const [applyCodeMutation, { data, loading, error }] = useMarketingApplyCodeMutation();

    return {
        applyCode: applyCodeMutation,
        applyCodeResult: data?.marketingApplyCode ?? null,
        loading,
        error,
    };
};

/**
 * Hook for removing an applied code (voucher or coupon) via Marketing Service.
 */
export const useMarketingRemoveCode = () => {
    const [removeCodeMutation, { data, loading, error }] = useMarketingRemoveCodeMutation();

    return {
        removeCode: removeCodeMutation,
        removeCodeResult: data?.marketingRemoveCode ?? null,
        loading,
        error,
    };
};

/**
 * Hook for applying a voucher from an allocation (by allocation ID).
 */
export const useApplyVoucherFromAllocation = () => {
    const [applyVoucherMutation, { data, loading, error }] = useMarketingApplyVoucherFromAllocationMutation();

    return {
        applyVoucherFromAllocation: applyVoucherMutation,
        applyVoucherResult: data?.marketingApplyVoucherFromAllocation ?? null,
        loading,
        error,
    };
};

/**
 * Hook for removing an applied voucher.
 */
export const useRemoveAppliedVoucher = () => {
    const [removeVoucherMutation, { data, loading, error }] = useMarketingRemoveAppliedVoucherMutation();

    return {
        removeAppliedVoucher: removeVoucherMutation,
        removeVoucherResult: data?.marketingRemoveAppliedVoucher ?? null,
        loading,
        error,
    };
};
